import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppLayout } from '@/layouts/AppLayout'
import { LoginLayout } from '@/layouts/LoginLayout'
import { ErrorPage } from '@/pages/ErrorPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { Home } from '@/pages/Home'
import { ProgramTLDP } from '@/pages/ProgramTLDP'
import { ProgramTBIGIn } from '@/pages/ProgramTBIGIn'
import { VacancyIdRedirect } from '@/pages/vacancy/VacancyIdRedirect'
import { VacancyIndex } from '@/pages/vacancy/VacancyIndex'
import { VacancyInfo } from '@/pages/vacancy/VacancyInfo'
import { ApplyForm } from '@/pages/vacancy/ApplyForm'
import { ProfileForm } from '@/pages/profile/ProfileForm'
import { ChangePassword } from '@/pages/profile/ChangePassword'
import { SavedVacancy } from '@/pages/profile/SavedVacancy'
import { ApplicationHistory } from '@/pages/profile/ApplicationHistory'
import { RequireAuth } from '@/auth/RequireAuth'
import { Login } from '@/pages/account/Login'
import { Register } from '@/pages/account/Register'
import { ForgotPassword } from '@/pages/account/ForgotPassword'
import { ResetPassword } from '@/pages/account/ResetPassword'
import { ConfirmEmail } from '@/pages/account/ConfirmEmail'
import { ConfirmEmailRegist } from '@/pages/account/ConfirmEmailRegist'

/**
 * Every path here is the one ASP.NET routing produced, so existing links,
 * bookmarks and emailed URLs keep resolving:
 *   default route  {controller=Home}/{action=Index}/{id?}  -> /, /Account/Login
 *   [Route("Profile")] + [Route("FormProfile")]            -> /Profile/FormProfile
 *   [Route("Vacancy")] + [Route("Info/{encoded}")]         -> /Vacancy/Info/<encoded>
 *
 * Every route is ported, with the Razor view each replaces:
 *
 *   /Vacancy                             Views/Vacancy/{Index,IndexBeforeLogin}.cshtml
 *   /Vacancy/Info/:encoded               Views/Vacancy/Info.cshtml
 *   /Vacancy/Form/:encoded    [auth]     Views/Vacancy/{Professional,Internship,TLDP}/Form.cshtml
 *   /Profile/FormProfile      [auth]     Views/Profile/Form.cshtml
 *   /Profile/ChangePassword   [auth]     Views/Profile/ChangePassword.cshtml
 *   /Profile/ApplicationHistory [auth]   Views/Profile/ApplicationHistory.cshtml
 *   /Profile/SavedVacancy     [auth]     Views/Profile/SavedVacancy.cshtml
 *
 * Scroll position is reset per navigation by <ScrollRestoration /> in the two
 * layouts, not here — see the note in AppLayout.
 *
 * Two ProfileController routes are deliberately NOT on that list: `Index`
 * ("/Profile") and `Edit` ("/Profile/EditProfile") both did `return View()`
 * against a view that does not exist in Views/Profile, so they threw at runtime
 * in the original — there is nothing to port. Views/Registration/Profile.cshtml
 * is likewise orphaned: no controller ever rendered it.
 */
export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    errorElement: <ErrorPage />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/Home/Index', element: <Navigate to="/" replace /> },

      { path: '/Program/tldp', element: <ProgramTLDP /> },
      { path: '/Program/tbigin', element: <ProgramTBIGIn /> },

      { path: '/Vacancy', element: <VacancyIndex /> },
      { path: '/Vacancy/Info/:encodedTrxVacancyID', element: <VacancyInfo /> },
      {
        path: '/Vacancy/Form/:encodedTrxVacancyID',
        element: (
          <RequireAuth>
            <ApplyForm />
          </RequireAuth>
        ),
      },

      // The two "plain id in, encoded id out" bounces. They exist so a link
      // carrying a raw id still lands on the canonical encoded URL.
      { path: '/Vacancy/InfoRedirect/:trxVacancyID', element: <VacancyIdRedirect target="Info" /> },
      { path: '/Vacancy/Apply/:trxVacancyID', element: <VacancyIdRedirect target="Form" /> },

      {
        path: '/Profile/FormProfile',
        element: (
          <RequireAuth>
            <ProfileForm />
          </RequireAuth>
        ),
      },
      {
        path: '/Profile/ChangePassword',
        element: (
          <RequireAuth>
            <ChangePassword />
          </RequireAuth>
        ),
      },
      {
        path: '/Profile/SavedVacancy',
        element: (
          <RequireAuth>
            <SavedVacancy />
          </RequireAuth>
        ),
      },
      {
        path: '/Profile/ApplicationHistory',
        element: (
          <RequireAuth>
            <ApplicationHistory />
          </RequireAuth>
        ),
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    // The Account pages render into _LoginLayout — a centred card with no
    // navbar or footer.
    element: <LoginLayout />,
    errorElement: <ErrorPage />,
    children: [
      { path: '/Account/Login', element: <Login /> },
      { path: '/Account/Register', element: <Register /> },
      { path: '/Account/ForgotPassword', element: <ForgotPassword /> },
      { path: '/Account/ResetPassword', element: <ResetPassword /> },
      { path: '/Account/ConfirmEmail', element: <ConfirmEmail /> },
      { path: '/Account/ConfirmEmailRegist', element: <ConfirmEmailRegist /> },
    ],
  },
])
